import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { ExplainerCtaConfig } from '@arthelper/shared/feature-wizard-config';

import { FeatureWizardStep } from '../../../components/featureWizard/FeatureWizardStep';

/**
 * Every caption ends with this, because both things it says are true of every preview: an
 * authoring screen that recorded events would corrupt the funnel this feature exists to read,
 * and an author checking their copy should not be navigated out of the admin screen.
 */
const INERT_NOTE = '· nothing is recorded, and the last step’s button closes this instead of navigating.';

const SOURCE_LABELS = {
  editor: 'Showing unsaved edits',
  saved: 'Showing the saved wizard',
};

/**
 * The authoring preview. It renders the SHARED step component, so what an author sees is the
 * component the overlay renders — a preview that re-implements the step is a preview that lies
 * the first time either side changes.
 *
 * It shows the panel over a plain dimmed backdrop with no mock page behind it: the question it
 * answers is what the wizard looks like, and a wizard is not tied to a page anyway.
 */
export function WizardPreviewModal({
  steps,
  initialStepIndex,
  source,
  onClose,
}: {
  steps: ExplainerCtaConfig[];
  initialStepIndex: number;
  /** Which of the two the author is looking at. They must never be mistaken for each other. */
  source: 'editor' | 'saved';
  onClose: () => void;
}) {
  const total = steps.length;
  const lastIndex = Math.max(total - 1, 0);
  const [index, setIndex] = useState(() => Math.min(Math.max(initialStepIndex, 0), lastIndex));

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') setIndex((current) => Math.max(current - 1, 0));
      if (event.key === 'ArrowRight') setIndex((current) => Math.min(current + 1, lastIndex));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, lastIndex]);

  const current = steps[index];
  if (!current) return null;

  // Inert on purpose: forward on every step but the last, and close on the last rather than
  // following the wizard's hand-off.
  const onPrimary = () => {
    if (index >= lastIndex) {
      onClose();
      return;
    }
    setIndex(index + 1);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Feature wizard preview"
    >
      <button
        type="button"
        onClick={() => setIndex((currentIndex) => Math.max(currentIndex - 1, 0))}
        disabled={index === 0}
        aria-label="Previous step"
        className="fixed left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-lg transition-opacity hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-25"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      <button
        type="button"
        onClick={() => setIndex((currentIndex) => Math.min(currentIndex + 1, lastIndex))}
        disabled={index >= lastIndex}
        aria-label="Next step"
        className="fixed right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-lg transition-opacity hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-25"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* No padding here: the shared step component owns the panel's 20px. */}
      <div className="relative w-full max-w-[460px] rounded-xl bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          className="absolute right-2 top-2 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
        >
          <X className="h-4 w-4" />
        </button>
        <FeatureWizardStep config={current} position={index + 1} total={total} onPrimary={onPrimary} />
      </div>

      <p className="mt-3 max-w-[460px] text-center text-xs leading-relaxed text-white/80">
        {SOURCE_LABELS[source]} {INERT_NOTE}
      </p>
    </div>
  );
}
